'use client';

import { useMemo } from 'react';
import { PatternCard } from './PatternCard';
import { PatternCardSkeleton } from './PatternCardSkeleton';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import type { Pattern } from '@/lib/supabase';

interface PatternGridProps {
  patterns?: Pattern[];
  limit?: number;
  isLoading?: boolean;
}

export function PatternGrid({ patterns, limit, isLoading }: PatternGridProps) {
  // Memoize the filtered patterns to avoid unnecessary re-slicing
  const displayPatterns = useMemo(() => {
    if (!patterns) return undefined;
    return limit ? patterns.slice(0, limit) : patterns;
  }, [patterns, limit]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(limit || 6)].map((_, i) => (
          <PatternCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!displayPatterns || displayPatterns.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400 text-lg mb-4">No patterns found</p>
        <p className="text-gray-500 text-sm">Try adjusting your search or filters</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {displayPatterns.map((pattern) => (
        <ErrorBoundary
          key={pattern.id}
          fallback={
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 text-center">
              <p className="text-gray-400 text-sm">Failed to load pattern</p>
            </div>
          }
        >
          <PatternCard pattern={pattern} />
        </ErrorBoundary>
      ))}
    </div>
  );
}
